import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import axios from "axios";

interface User {
  id: string;
  role: string;
}

interface AuthContextType {
  user: User | null;
  login: (password: string) => Promise<boolean>;
  logout: () => void;
  loading: boolean;
  token: string | null;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem("wa_token");
    if (stored) {
      setToken(stored);
      axios.get("/api/auth/me", { headers: { Authorization: `Bearer ${stored}` } })
        .then(res => {
          if (res.data.ok) setUser(res.data.user);
          else localStorage.removeItem("wa_token");
        })
        .catch(() => localStorage.removeItem("wa_token"))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (password: string): Promise<boolean> => {
    try {
      const res = await axios.post("/api/auth/login", { password });
      if (res.data.ok) {
        setToken(res.data.token);
        setUser(res.data.user);
        localStorage.setItem("wa_token", res.data.token);
        axios.defaults.headers.common["Authorization"] = `Bearer ${res.data.token}`;
        return true;
      }
    } catch {
      return false;
    }
    return false;
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    localStorage.removeItem("wa_token");
    delete axios.defaults.headers.common["Authorization"];
  };

  useEffect(() => {
    if (token) axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  }, [token]);

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, token }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
